/**
 * @fileoverview Multi-student Teacher Assessment Drawer (AR-539).
 *
 * Opens from:
 *   - Tasks Tracker (cell click) → initialStudentId pre-selected
 *   - Project View "Assess students" button → defaultAllStudents
 *
 * UI source of truth: arcmosaico-prototype TeacherAssessmentDrawer
 * (mode='activity'). Backend wiring: AR-538 list/update endpoints.
 */

import { RefObject, useEffect, useMemo, useRef, useState } from 'react'
import {
  ActionIcon,
  Box,
  Center,
  Group,
  Loader,
  Stack,
  Text,
  Title,
} from '@mantine/core'
import { IconX } from '@tabler/icons-react'
import { useTranslation } from 'react-i18next'
import LumiarDrawer from 'components/LumiarDrawer/LumiarDrawer'
import { AssessmentItemCard } from './components/AssessmentItemCard'
import { AssessmentNotesSection } from './components/AssessmentNotesSection'
import { ClassroomGradeSection } from './components/ClassroomGradeSection'
import { DisableConfirmModal } from './components/DisableConfirmModal'
import { OverwriteConfirmModal } from './components/OverwriteConfirmModal'
import { ProjectLessonInfoBox } from './components/ProjectLessonInfoBox'
import { StudentMultiSelect } from './components/StudentMultiSelect'
import { shouldShowActivityNameInDeliverable } from './components/DeliverableInfo'
import { useTeacherAssessmentDrawer } from './useTeacherAssessmentDrawer'
import {
  fetchLessonAssessmentJournal,
  updateLessonAssessmentJournal,
} from './service'
import type { LessonAssessmentJournal } from './service'
import type { AssessmentItem } from './types'

/** Drawer outer width. Internal content needs:
 *    grid (980px) + outer Stack p="lg" (32×2=64) + outer Paper p="md" (24×2=48)
 *    + outer Paper left accent border (4) + standard borders (~3) + inner Paper
 *    p="md" (24×2=48) + inner Paper borders (~2) ≈ 1149.
 *  We round up to 1200 for a comfortable badge buffer; clamp to `100vw`. */
const DRAWER_WIDTH = 'min(1200px, 100vw)'

export interface TeacherAssessmentDrawerProps {
  opened: boolean
  sessionMeetingId: string
  /** When supplied, that student is the only one selected on open. */
  initialStudentId?: string | null
  /** Project View entry point — open with "All students" pre-selected. */
  defaultAllStudents?: boolean
  /** Shown as "Assessment - <Activity>" when a specific activity triggered the open. */
  activityTitle?: string | null
  onClose: () => void
  /** Called once the user closes the drawer; parent typically refreshes its grid. */
  onClosed?: () => void
}

function buildKey(item: AssessmentItem): string {
  return `${item.activityId}:${item.lv4ItemId}`
}

export function TeacherAssessmentDrawer({
  opened,
  sessionMeetingId,
  initialStudentId,
  defaultAllStudents,
  activityTitle,
  onClose,
  onClosed,
}: TeacherAssessmentDrawerProps) {
  const { t } = useTranslation()
  const modalRef = useRef<HTMLElement | null>(null)

  const {
    loading,
    data,
    selectedStudentIds,
    visibleStudents,
    studentOptions,
    setStudentSelection,
    overwritePrompt,
    dismissOverwritePrompt,
    confirmOverwritePrompt,
    disablePrompt,
    dismissDisablePrompt,
    confirmDisablePrompt,
    setStudentScore,
    applyBulkScore,
    toggleItemEnabled,
    toggleStudentEnabled,
  } = useTeacherAssessmentDrawer({
    opened,
    sessionMeetingId,
    initialStudentId,
    defaultAllStudents,
  })

  const handleDrawerChange = (value: boolean) => {
    if (value) return
    onClose()
    onClosed?.()
  }

  const showActivityNameInDeliverable = useMemo(() => {
    if (!data) return false
    return shouldShowActivityNameInDeliverable([...data.standards, ...data.skills])
  }, [data])

  // AR-805: Classroom-linked assignments = distinct deliverable-enabled
  // activities of the lesson. Stage 1 derives them from the drawer data; the
  // stage 2 payload returns them explicitly (prototype: classroom_assignments).
  const classroomAssignments = useMemo(() => {
    if (!data) return []
    const byActivity = new Map<string, string>()
    for (const item of [...data.standards, ...data.skills]) {
      if (item.activityHasDeliverable) byActivity.set(item.activityId, item.activityTitle)
    }
    return Array.from(byActivity, ([activityId, activityTitle]) => ({ activityId, activityTitle }))
  }, [data])

  // Teacher notes (AR-753) are per-assessment — one per student per lesson —
  // so the section only renders when exactly one student is selected (bulk
  // mode awaits a UX definition).
  const singleStudent = visibleStudents.length === 1 ? visibleStudents[0] : null

  // The assessment id is the same across every item for a given student, so
  // the first score found is enough.
  const singleStudentAssessmentId = useMemo(() => {
    if (!data || !singleStudent) return null
    const item = [...data.standards, ...data.skills].find(i => i.perStudent[singleStudent.id])
    return item?.perStudent[singleStudent.id].score.assessmentId ?? null
  }, [data, singleStudent])

  const [journal, setJournal] = useState<LessonAssessmentJournal | null>(null)
  const [journalLoading, setJournalLoading] = useState(false)

  // Load the master journal for the selected student (cancelled-flag cleanup,
  // same pattern as the drawer data load in useTeacherAssessmentDrawer).
  useEffect(() => {
    if (!opened || !singleStudentAssessmentId) {
      setJournal(null)
      return
    }
    let cancelled = false
    setJournalLoading(true)
    setJournal(null)
    fetchLessonAssessmentJournal({ sessionMeetingId, assessmentId: singleStudentAssessmentId })
      .then(payload => {
        if (!cancelled) setJournal(payload)
      })
      .catch(() => {
        if (!cancelled) setJournal(null)
      })
      .finally(() => {
        if (!cancelled) setJournalLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [opened, sessionMeetingId, singleStudentAssessmentId])

  const title = activityTitle
    ? t('arc_teacher_assessment_drawer.title_with_activity', { activityTitle })
    : t('arc_teacher_assessment_drawer.title')

  const renderItems = (items: AssessmentItem[]) =>
    items.map(item => {
      const key = buildKey(item)
      return (
        <AssessmentItemCard
          key={key}
          item={item}
          itemKey={key}
          visibleStudents={visibleStudents}
          showDeliverableColumn={item.activityHasDeliverable}
          showActivityNameInDeliverable={showActivityNameInDeliverable}
          onStudentScoreChange={(studentId, score) => void setStudentScore(key, studentId, score)}
          onBulkScore={score => void applyBulkScore(key, score)}
          onToggleEnabled={enabled => void toggleItemEnabled(key, enabled)}
          onToggleStudentEnabled={(studentId, enabled) =>
            void toggleStudentEnabled(key, studentId, enabled)
          }
        />
      )
    })

  return (
    <>
      <LumiarDrawer
        visibility={opened}
        onChange={handleDrawerChange}
        side="right"
        modalRef={modalRef as RefObject<HTMLDivElement>}
        scrollable={false}
      >
        <Box
          style={{
            width: DRAWER_WIDTH,
            height: '100vh',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
          }}
        >
          <Group
            position="apart"
            align="center"
            px="lg"
            py="md"
            style={{ borderBottom: '1px solid var(--mantine-color-gray-2)', flexShrink: 0 }}
          >
            <Title order={3}>{title}</Title>
            <ActionIcon
              variant="subtle"
              onClick={() => handleDrawerChange(false)}
              aria-label={t('arc_teacher_assessment_drawer.close_aria')}
            >
              <IconX size={20} />
            </ActionIcon>
          </Group>

          <Box
            style={{
              flex: 1,
              minHeight: 0,
              overflowY: 'auto',
              overflowX: 'hidden',
            }}
          >
            <Stack
              spacing="lg"
              p="lg"
              style={{ width: '100%', maxWidth: '100%', boxSizing: 'border-box' }}
            >
              {loading && (
                <Center py="xl">
                  <Loader size="md" />
                </Center>
              )}

              {!loading && data && (
                <>
                  <ProjectLessonInfoBox
                    projectName={data.project.name}
                    lessonTitle={data.lesson.title}
                  />

                  <StudentMultiSelect
                    options={studentOptions}
                    value={selectedStudentIds}
                    onChange={setStudentSelection}
                    disabled={data.students.length === 0}
                  />

                  {data.standards.length === 0 && data.skills.length === 0 && (
                    <Text color="dimmed" align="center" py="xl">
                      {data.ungeneratedItemsCount > 0
                        ? t('arc_teacher_assessment_drawer.assessments_not_ready')
                        : t('arc_teacher_assessment_drawer.no_curriculum_items')}
                    </Text>
                  )}

                  {data.standards.length > 0 && (
                    <Stack spacing="md">
                      <Title order={5}>{t('arc_teacher_assessment_drawer.standards')}</Title>
                      {renderItems(data.standards)}
                    </Stack>
                  )}

                  {data.skills.length > 0 && (
                    <Stack spacing="md">
                      <Title order={5}>{t('arc_teacher_assessment_drawer.skills')}</Title>
                      {renderItems(data.skills)}
                    </Stack>
                  )}

                  <ClassroomGradeSection
                    assignments={classroomAssignments}
                    students={visibleStudents}
                  />

                  {singleStudent && singleStudentAssessmentId && !journalLoading &&
                    journal?.journalId && (
                    <AssessmentNotesSection
                      key={`${singleStudent.id}:${journal.journalId}`}
                      initialValue={journal.description}
                      onPersist={description =>
                        updateLessonAssessmentJournal({
                          studentId: singleStudent.id,
                          assessmentId: singleStudentAssessmentId,
                          journalId: journal.journalId,
                          description,
                        })
                      }
                    />
                  )}
                </>
              )}

              {!loading && !data && (
                <Center py="xl">
                  <Text color="dimmed">{t('arc_teacher_assessment_drawer.error.load_failed')}</Text>
                </Center>
              )}
            </Stack>
          </Box>
        </Box>
      </LumiarDrawer>

      <OverwriteConfirmModal
        opened={overwritePrompt !== null}
        affectedCount={overwritePrompt?.affectedStudentIds.length ?? 0}
        onCancel={dismissOverwritePrompt}
        onConfirm={confirmOverwritePrompt}
      />

      <DisableConfirmModal
        opened={disablePrompt !== null}
        title={
          disablePrompt?.kind === 'student'
            ? t('arc_teacher_assessment_drawer.disable_student_modal.title')
            : t('arc_teacher_assessment_drawer.disable_item_modal.title')
        }
        body={
          disablePrompt?.kind === 'student'
            ? t('arc_teacher_assessment_drawer.disable_student_modal.body', {
              studentName: disablePrompt.studentName,
            })
            : disablePrompt && disablePrompt.affectedStudentIds.length === 1
              ? t('arc_teacher_assessment_drawer.disable_item_modal.body_single')
              : t('arc_teacher_assessment_drawer.disable_item_modal.body_many', {
                count: disablePrompt?.affectedStudentIds.length ?? 0,
              })
        }
        cancelLabel={
          disablePrompt?.kind === 'student'
            ? t('arc_teacher_assessment_drawer.disable_student_modal.cancel')
            : t('arc_teacher_assessment_drawer.disable_item_modal.cancel')
        }
        confirmLabel={
          disablePrompt?.kind === 'student'
            ? t('arc_teacher_assessment_drawer.disable_student_modal.confirm')
            : t('arc_teacher_assessment_drawer.disable_item_modal.confirm')
        }
        onCancel={dismissDisablePrompt}
        onConfirm={confirmDisablePrompt}
      />
    </>
  )
}
